import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronDown, MessageCircle, Mail, ShieldAlert } from "lucide-react";
import Seo from "@/components/Seo";

const FAQS = [
  {
    q: "How do I sign up?",
    a: "Click Sign In on the top right and continue with your Google account. After signing in, pick your role: Hobby Farmer, Driver, Client, or Wholesale. You'll receive a welcome email immediately.",
  },
  {
    q: "How is my driver verified?",
    a: "Every driver uploads a valid driver's license at sign-up. Our admin team reviews and approves it before the driver can accept pickups. You'll see a 'Verified' badge on your driver's profile inside the dashboard.",
  },
  {
    q: "How do drivers get paid?",
    a: "Once an order is delivered and funds are confirmed by our admin team, your earnings (including 100% of any tips) are deposited to your PayPal or CashApp account on file. You can see every payout in the Ledger section of your driver dashboard.",
  },
  {
    q: "What is order verification & confirmation?",
    a: "When a client pays, our admin team verifies the funds, marks the order as confirmed, and triggers the deposit to the driver and hobby farmer. Both parties get an email + in-app notification at the moment confirmation happens.",
  },
  {
    q: "Can I report a driver or a hobby farmer?",
    a: "Yes. On any user's profile, in your messages, or from your order history, click 'Report' and tell us what happened. Admins review every report within 48 hours. Repeat offenders are removed from the network.",
  },
  {
    q: "What about mutation / deformed produce?",
    a: "Hobby farmers list odd-shaped or imperfect produce in the 'Mutation' category — free or barter only. It's the same quality as conventional produce; it just looks different. Great food, zero waste.",
  },
  {
    q: "Can a driver eat the farmer's produce on the way?",
    a: "No. The produce belongs to the client. If a hobby farmer chooses to gift food to a driver after the delivery, that's between the two of them — never help yourself.",
  },
  {
    q: "What does the membership include?",
    a: "Members get reduced order fees, member-only listings, early access to new hobby farmers, and our weekly digest. We run a yearly and a monthly plan — see /pricing for current rates and promotions.",
  },
  {
    q: "How do hobby farmers get paid?",
    a: "Hobby farmers can either use their own payment system (Venmo, Zelle, Square, etc. listed on their public profile) or accept payouts through Azure Gypsy Farms once the admin team verifies the funds. Taxes are paid directly to government agencies by each hobby farmer.",
  },
  {
    q: "Is the app available on mobile?",
    a: "Yes. The site works in any phone browser, and you can install Azure Gypsy Farms to your home screen as a Progressive Web App (look for the 'Install' banner, or tap your browser's 'Add to Home Screen' option).",
  },
  {
    q: "How do I become a wholesale buyer?",
    a: "Visit /wholesale and submit the application form. Our team reviews wholesale applications within 2–3 business days and emails you an approval with bulk pricing.",
  },
  {
    q: "I'm a hobby farmer — how do I get verified?",
    a: "After signing up, upload your government-issued ID from your dashboard. Once admins review and approve, you'll see the 'Verified' badge on your public profile, which gives clients confidence to order.",
  },
];

function Item({ q, a, idx, openIdx, setOpenIdx }) {
  const isOpen = openIdx === idx;
  return (
    <div className="border-b border-stone-200 last:border-b-0" data-testid={`faq-item-${idx}`}>
      <button
        type="button"
        onClick={() => setOpenIdx(isOpen ? -1 : idx)}
        className="w-full text-left py-5 px-1 flex justify-between items-start gap-4 group"
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="font-serif text-lg text-stone-900 group-hover:text-emerald-800 transition-colors">{q}</span>
        <ChevronDown
          size={20}
          className={`shrink-0 mt-1 text-stone-500 transition-transform ${isOpen ? "rotate-180 text-emerald-800" : ""}`}
        />
      </button>
      {isOpen && (
        <div className="pb-6 px-1 text-stone-600 font-sans text-sm leading-relaxed">{a}</div>
      )}
    </div>
  );
}

export default function Faq() {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div>
      <Seo
        url="/faq"
        title="FAQ & Support"
        description="Frequently asked questions about Azure Gypsy Farms — hobby farmer verification, driver payouts, order confirmation, mutation produce, memberships and more."
      />
      <section className="max-w-4xl mx-auto px-6 sm:px-12 pt-20 pb-12">
        <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">Support</div>
        <h1 className="mt-3 font-serif text-5xl sm:text-6xl text-stone-900">FAQ &amp; help.</h1>
        <p className="mt-4 font-sans text-stone-600 max-w-2xl">
          Quick answers to the questions we hear the most. Can't find what you need? Send us a chat message — a real human reads every one.
        </p>
      </section>

      <section className="max-w-4xl mx-auto px-6 sm:px-12 pb-12">
        <div className="bg-white border border-stone-200 rounded-md px-6 sm:px-8">
          {FAQS.map((f, i) => (
            <Item key={i} idx={i} q={f.q} a={f.a} openIdx={openIdx} setOpenIdx={setOpenIdx} />
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 sm:px-12 pb-24">
        <div className="bg-stone-900 rounded-md p-10 sm:p-14 grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <div className="text-[11px] uppercase tracking-[0.3em] text-amber-300 font-semibold">Still need help?</div>
            <h3 className="mt-3 font-serif text-3xl text-stone-50">Chat with support.</h3>
            <p className="mt-3 font-sans text-stone-300 text-sm leading-relaxed max-w-lg">
              We typically reply within a few hours. Use the form for anything not covered above — order issues, payout questions, suspicious accounts, or to report a hobby farmer or driver.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/contact">
                <Button data-testid="faq-chat-support" className="bg-emerald-700 hover:bg-emerald-600 text-stone-50 px-6 py-5 rounded-md">
                  <MessageCircle size={16} className="mr-2" /> Chat with Support
                </Button>
              </Link>
              <a href="mailto:support@azuregypsy.org">
                <Button data-testid="faq-email-support" variant="outline" className="bg-stone-50/5 text-stone-50 border-stone-50/30 hover:bg-stone-50/10 px-6 py-5 rounded-md">
                  <Mail size={16} className="mr-2" /> Email Support
                </Button>
              </a>
            </div>
          </div>
          <div className="bg-stone-800 border border-stone-700 rounded-md p-6">
            <div className="flex items-center gap-2 text-amber-300">
              <ShieldAlert size={18} /> <span className="text-xs uppercase tracking-[0.2em] font-semibold">Report a user</span>
            </div>
            <p className="mt-3 text-stone-300 text-sm font-sans leading-relaxed">
              See something wrong? Report a hobby farmer or driver directly from their profile, or send us the details via the contact form. We act on every report.
            </p>
            <Link to="/contact" state={{ subject: "Report a user" }}>
              <Button data-testid="faq-report-user" size="sm" className="mt-4 bg-amber-700 hover:bg-amber-600 text-stone-50 w-full">
                Report a user
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
